import React, { useEffect, useState } from 'react';
import ModalComponent from '../../components/ModalComponent';
import Button from 'react-bootstrap/Button';
import '../../assets/style/Modal.css';
import { FormGroup, Input, Label } from 'reactstrap';
import ModalHeader from '../../components/ModalHeader';
import { CLICK_ACTIONS, KYC_DOCUMENT_STATUS } from '../../utils/Constant';
import CenterSpinner from '../../components/CenterSpinner';
import {
  NoteType,
  KYCDocumentDetailsType,
  DocumentType,
} from '../../utils/Types';
import { getNoteDate } from '../../utils/Util';
import { useAppDispatch, useAppSelector } from '../../store/redux-hooks';
import { useLoader } from '../../loader/LoaderProvider';
import ImagePreviewModal from './ImagePreviewModal';
import { saveComment } from '../onboarding/OnboardingPageHelper';
import BasicDetails from '../../components/BasicDetails';
import {
  getDocumentDetails,
  kycDocumentDetailsSelector,
} from '../../features/onboardingSlice';

import { candidateSelector } from '../../features/assignGridSlice';

interface KYCDocDetailsModalProps {
  isOpen: boolean;
  selectedDocument?: DocumentType | undefined;
  toggleApproveModal: (action: string) => void;
  photoURL?: string | undefined;
  onApproveRejectAction: (
    iibApprovalStatusType: number | undefined,
    approvalStatusType: number,
    documentId: number | undefined,
    comment: string
  ) => void;
  candidateId?: number;
  roleId?: number;
}

const KYCDocDetailsModal: React.FunctionComponent<KYCDocDetailsModalProps> = ({
  isOpen,
  selectedDocument,
  toggleApproveModal,
  photoURL,
  onApproveRejectAction,
  candidateId,
  roleId,
}: KYCDocDetailsModalProps) => {
  const [isDocPreviewModalOpen, setIsDocPreviewModalOpen] =
    useState<boolean>(false);
  const { showLoader, setShowLoader } = useLoader();
  const dispatch = useAppDispatch();
  const [comment, setComment] = useState<string>('');
  const userDetails: any = useAppSelector(candidateSelector).user;
  const [photo, setPhoto] = useState<any>();

  //get notes from BASS
  const documentDetails: KYCDocumentDetailsType = useAppSelector(
    kycDocumentDetailsSelector
  )?.result?.document;
  const notes: NoteType[] =
    useAppSelector(kycDocumentDetailsSelector)?.result?.notes || [];

  const toggleDocPreviewModal = () => {
    setIsDocPreviewModalOpen(!isDocPreviewModalOpen);
  };

  /**
   * Call dispatch action for calling API to get candidate details.
   */
  //get document details from BASS
  const getDocDetails = async () => {
    if (candidateId && selectedDocument?.documentId) {
      try {
        await dispatch(
          getDocumentDetails({
            candidateId,
            documentId: selectedDocument.documentId,
          })
        );
      } finally {
        setShowLoader(false);
      }
    }
  };

  const onAction = (
    action: string,
    documentId: number | undefined,
    comment: string
  ) => {
    const iibApprovalStatus: any = null;
    onApproveRejectAction(
      action == CLICK_ACTIONS.APPROVE ? 3 : 4,
      iibApprovalStatus,
      documentId,
      comment
    );

    toggleApproveModal(CLICK_ACTIONS.REFRESH_LIST);
  };

  useEffect(() => {
    if (selectedDocument?.documentId) {
      setShowLoader(true);
      getDocDetails();
    }
  }, [selectedDocument?.documentId]);

  useEffect(() => {
    if (isOpen) {
      setComment('');
    }
  }, [isOpen]);

  const getCommentAndAction = () => (
    <>
      <div className="bg-comment p-4 my-2 mx-4 rounded">
        <div className="mb-3">
          <FormGroup>
            <Label for="comment" className="model-comment-title">
              Comments &#40;<span className="text-danger ">* </span>
              {''} Mandatory for Rejection&#41;
            </Label>
            <Input
              disabled={roleId != 15 && roleId != 41 ? true : false}
              id="comment"
              name="comment"
              type="textarea"
              maxLength={250}
              value={comment}
              placeholder="Please Comment"
              className="rounded border model-comment-area"
              onChange={(event) => setComment(event.target.value)}
            />
          </FormGroup>
          <Button
            disabled={roleId != 15 && roleId != 41 ? true : false}
            variant="primary"
            className="cl-btn-primary"
            onClick={() => {
              // new func to save logs with doc id
              saveComment(
                comment,
                candidateId,
                dispatch,
                setShowLoader,
                getDocDetails,
                setComment,
                selectedDocument?.documentId
              );
            }}
          >
            Save
          </Button>
        </div>

        <div className="bg-white rounded border px-4 pt-3 pb-3">
          <Label for="comment" className="model-comment-title">
            Log Details
          </Label>
          <div className="modal-log-div">
            <ul className="li-item model-log-value">
              {notes &&
                notes.length > 0 &&
                notes?.map((noteItem, index) => (
                  <li className="mt-1" key={index}>
                    {getNoteDate(noteItem)}
                    {noteItem.notesdescription}
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-center">
        {selectedDocument?.documentStatus ===
          String(KYC_DOCUMENT_STATUS.APPROVED) ||
        selectedDocument?.documentStatus ===
          String(KYC_DOCUMENT_STATUS.REJECT) ? (
          <Button
            variant="primary"
            className="cl-btn-primary"
            onClick={() => {
              toggleApproveModal(CLICK_ACTIONS.NO_ACTION);
            }}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              disabled={roleId != 15 && roleId != 41 ? true : false}
              variant="primary"
              className="m-1 cl-btn-primary"
              onClick={() => {
                onAction(
                  CLICK_ACTIONS.APPROVE,
                  selectedDocument?.documentId,
                  comment
                );
              }}
            >
              Approve
            </Button>
            <Button
              variant="outline-primary"
              className="m-1 cl-btn-outline-primary"
              onClick={() => {
                onAction(
                  CLICK_ACTIONS.REJECT,
                  selectedDocument?.documentId,
                  comment
                );
                setComment('');
              }}
              disabled={
                !comment.trim() || (roleId != 15 && roleId != 41) ? true : false
              }
            >
              Reject
            </Button>
          </>
        )}
      </div>
    </>
  );

  return (
    <>
      <ModalComponent
        isOpen={isOpen}
        toggle={() => {
          toggleApproveModal(CLICK_ACTIONS.NO_ACTION);
        }}
        className="modalWrapper"
        backdrop="static"
      >
        <div className="bg-white p-2 rounded border">
          <ModalHeader
            photoUrl={photoURL}
            toggle={() => {
              toggleApproveModal(CLICK_ACTIONS.NO_ACTION);
            }}
            enlargePhoto={(photo) => {
              setPhoto(photo);
              toggleDocPreviewModal();
            }}
            docURLBack={selectedDocument?.documentUrlBack}
          />
          {showLoader ? (
            <CenterSpinner showLoader={true} />
          ) : (
            selectedDocument?.documentId !== 5 && (
              <>
                <BasicDetails
                  documentDetails={documentDetails}
                  isEditable={true}
                />
                {getCommentAndAction()}
              </>
            )
          )}
        </div>
      </ModalComponent>
      <ImagePreviewModal
        isOpen={isDocPreviewModalOpen}
        onApproveRejectAction={() => {}}
        photoURL={photo}
        toggle={toggleDocPreviewModal}
      />
    </>
  );
};

export default KYCDocDetailsModal;
